import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "results",
    "mobileOverlay",
    "mobileInput",
    "mobileResults",
  ];

  static values = { url: String };

  connect() {
    this._onGlobalKeydown = this._handleGlobalKeydown.bind(this);
    this._onClickOutside = this._handleClickOutside.bind(this);
    document.addEventListener("keydown", this._onGlobalKeydown);
    document.addEventListener("click", this._onClickOutside);
    this._debounceTimer = null;
  }

  disconnect() {
    document.removeEventListener("keydown", this._onGlobalKeydown);
    document.removeEventListener("click", this._onClickOutside);
    clearTimeout(this._debounceTimer);
  }

  // ── Desktop search ──

  search() {
    clearTimeout(this._debounceTimer);
    const query = this.inputTarget.value.trim();

    if (query.length < 2) {
      this._hideResults();
      return;
    }

    this._debounceTimer = setTimeout(() => {
      this._fetchResults(query, this.resultsTarget);
    }, 300);
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this._hideResults();
      this.inputTarget.blur();
    }
  }

  // ── Mobile search overlay ──

  openMobile() {
    if (!this.hasMobileOverlayTarget) return;
    this.mobileOverlayTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      this.mobileInputTarget.focus();
    });
  }

  closeMobile() {
    if (!this.hasMobileOverlayTarget) return;
    this.mobileOverlayTarget.classList.add("hidden");
    document.body.style.overflow = "";
    this.mobileInputTarget.value = "";
    this.mobileResultsTarget.innerHTML = "";
  }

  searchMobile() {
    clearTimeout(this._debounceTimer);
    const query = this.mobileInputTarget.value.trim();

    if (query.length < 2) {
      this.mobileResultsTarget.innerHTML = "";
      return;
    }

    this._debounceTimer = setTimeout(() => {
      this._fetchResults(query, this.mobileResultsTarget);
    }, 300);
  }

  onMobileKeydown(event) {
    if (event.key === "Escape") {
      this.closeMobile();
    }
  }

  // ── Private ──

  _handleGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      // On mobile, open overlay; on desktop, focus input
      if (window.innerWidth <= 960 && this.hasMobileOverlayTarget) {
        this.openMobile();
      } else {
        this.inputTarget.focus();
        this.inputTarget.select();
      }
    }
  }

  _handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._hideResults();
    }
  }

  async _fetchResults(query, target) {
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const html = await response.text();
        target.innerHTML = html;
        if (target === this.resultsTarget) {
          this._showResults();
        }
      }
    } catch (_error) {
      if (target === this.resultsTarget) {
        this._hideResults();
      }
    }
  }

  _showResults() {
    this.resultsTarget.style.display = "block";
  }

  _hideResults() {
    this.resultsTarget.style.display = "none";
  }
}
